import React, { useState, useRef, useEffect } from "react";
import { FileDrop } from "./FileDrop";
import { Text } from "./Text";

// Keep in sync with LOGO_ALLOWED_CONTENT_TYPES in app/customers/views/account.py
const ACCEPTED_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
];

type LogoUploadProps = {
  inputId?: string;
};

export const LogoUpload: React.FC<LogoUploadProps> = ({
  inputId = "id_logo",
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [syncError, setSyncError] = useState(false);
  const hiddenInputRef = useRef<HTMLInputElement | null>(null);

  const assignFilesToInput = (
    input: HTMLInputElement,
    files: FileList
  ): boolean => {
    const descriptor = Object.getOwnPropertyDescriptor(
      Object.getPrototypeOf(input),
      "files"
    );
    if (descriptor?.set) {
      descriptor.set.call(input, files);
      return true;
    }
    return false;
  };

  useEffect(() => {
    const input = document.getElementById(inputId) as HTMLInputElement;
    if (input) {
      hiddenInputRef.current = input;
    }
  }, [inputId]);

  useEffect(() => {
    const hiddenInput = hiddenInputRef.current;
    if (!hiddenInput) return;

    if (selectedFile) {
      if (typeof DataTransfer === "undefined") {
        setSyncError(true);
        return;
      }
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(selectedFile);
      const success = assignFilesToInput(hiddenInput, dataTransfer.files);
      if (!success) {
        const emptyDataTransfer = new DataTransfer();
        assignFilesToInput(hiddenInput, emptyDataTransfer.files);
        setSyncError(true);
        return;
      }
      setSyncError(false);

      const url = URL.createObjectURL(selectedFile);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      if (typeof DataTransfer !== "undefined") {
        const emptyDataTransfer = new DataTransfer();
        assignFilesToInput(hiddenInput, emptyDataTransfer.files);
      }
      setPreviewUrl(null);
      setSyncError(false);
    }

    const event = new Event("change", { bubbles: true });
    hiddenInput.dispatchEvent(event);
  }, [selectedFile]);

  const isValidFile = (file: File | null): boolean => {
    if (!file) return true;
    return ACCEPTED_MIME_TYPES.includes(file.type);
  };

  const isInvalid = selectedFile ? !isValidFile(selectedFile) : false;

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <div className="tw-w-full tw-max-w-2xl tw-mx-auto">
        <FileDrop
          selectedFile={selectedFile}
          isInvalid={isInvalid}
          onChange={setSelectedFile}
          contentUnselected={
            <Text>Accepted formats: PNG, JPG, SVG, WebP, GIF. Max 5 MB.</Text>
          }
          contentSelected={
            previewUrl && !isInvalid ? (
              <div className="tw-mt-2">
                <img
                  src={previewUrl}
                  alt="Logo preview"
                  className="tw-max-h-24 tw-max-w-full tw-object-contain tw-mx-auto tw-rounded tw-border tw-border-gray-200"
                />
              </div>
            ) : null
          }
        />
      </div>
      {syncError && (
        <Text color="red">
          Drag and drop is not supported in this browser. Please use the Select
          file to upload button instead.
        </Text>
      )}
    </div>
  );
};
